import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
import {sendEvent} from '@github-ui/hydro-analytics'
import {instanceOfIssueFormData, type IssueCreatePayload} from '@github-ui/issue-create/Model'
import type {IssueFormElementRef, IssueFormRef} from '@github-ui/issue-form/Types'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {BannerProps} from '@primer/react'
import type {RefObject} from 'react'
import {useCallback, useEffect} from 'react'

import type {DraftOrExistingIssue} from '../content-preview-types'
import {type BulkCreateStatusResponse, useBulkCreateMutationWithStatus} from './use-bulk-create-mutation'
import {type TreeNode, visitDependencies} from './use-draft-issue-tree-map'
import {useOnSaveCallback} from './use-on-save-callback'

export class CreateError extends Error {
  readonly type: string
  readonly bannerTitle: string
  readonly bannerVariant: BannerProps['variant']

  constructor(message: string) {
    super(message)
    this.name = 'CreateError'
    this.type = 'creation'
    this.bannerTitle = 'Errors in issue creation'
    this.bannerVariant = 'critical'
  }
}

export class ValidationError extends Error {
  readonly type: string
  readonly bannerTitle: string
  readonly bannerVariant: BannerProps['variant']

  constructor(message: string) {
    super(message)
    this.name = 'ValidationError'
    this.type = 'validation'
    this.bannerTitle = 'Validation errors in issue content'
    this.bannerVariant = 'warning'
  }
}

export type SingleIssueCreateError = ValidationError | CreateError

export function useIssueCreationHandler({
  issueNode,
  onComplete,
  onError,
  issueFormRef,
  issueTemplate,
  formRepository,
}: {
  issueNode: TreeNode<DraftOrExistingIssue> | undefined
  onComplete: () => void
  onError?: (error: SingleIssueCreateError) => void
  issueFormRef?: RefObject<IssueFormRef | null>
  issueTemplate?: IssueCreatePayload | null
  formRepository?: RepositoryPickerRepository | undefined
}) {
  const {mutate, isLoading, data, isComplete, isError, error} = useBulkCreateMutationWithStatus({
    issueNode,
    issueFormRef,
    issueTemplate,
  })
  const onSaveSuccess = useOnSaveCallback()

  // Validation logic for single issue creation with focus behavior
  const validateSingleIssueCreation = useCallback(() => {
    if (!issueNode) return false

    const issue = issueNode.item

    // Only validate draft issues (existing issues don't need validation for creation)
    if (issue.type !== 'new-issue') {
      return true
    }

    const title = issue.name || ''
    let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

    if (!formRepository || !issue.repository) {
      onError?.(new ValidationError('Please select a repository for the issue before creating it.'))
      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_ISSUE_NO_REPO_SELECTED_ERROR',
        mode: 'immersive',
        issueTag: issue.tag,
        selectedRepository: formRepository?.nameWithOwner || 'none',
        suggestedRepository: issue.repository || 'none',
      })
      return false
    }

    // Validate repository mismatch between draft issue and form repository
    // This technically shouldn't happen because it should've triggered an onChange event that would update
    // both the draft issue and the form repository to be in sync, but just in case
    const issueRepoLower = issue.repository.toLowerCase()
    const formRepoLower = formRepository.nameWithOwner.toLowerCase()

    if (issueRepoLower !== formRepoLower) {
      onError?.(
        new ValidationError(
          `The selected repository (${formRepository.nameWithOwner}) doesn't match the repository Copilot suggested (${issue.repository}). Please select a repository you have access to.`,
        ),
      )
      // Log analytics event for repository mismatch
      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_ISSUE_REPO_MISMATCH_ERROR',
        mode: 'immersive',
        issueTag: issue.tag,
        selectedRepository: formRepository.nameWithOwner,
        suggestedRepository: issue.repository,
      })
      return false
    }

    // Validate issue title
    const titleValidationResult = validateIssueTitle(title)
    if (!titleValidationResult.isValid) {
      onError?.(new ValidationError(titleValidationResult.errorMessage || 'Issue title is invalid'))
      return false
    }

    // Validate issue body
    const bodyValidationResult = validateIssueBody(issue.body || '')
    if (!bodyValidationResult.isValid) {
      onError?.(new ValidationError(bodyValidationResult.errorMessage || 'Issue body is invalid'))
      return false
    }

    // If template is an issue form, validate form elements
    const isIssueForm = issueTemplate && instanceOfIssueFormData(issueTemplate.data)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (isIssueForm && issueFormRef!.current) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const invalidInputs = issueFormRef!.current.getInvalidInputs()
      if (invalidInputs.length > 0) {
        // Use first invalid input if there's no title ref yet
        refToFocusOnError = refToFocusOnError || invalidInputs[0]
        onError?.(new ValidationError('Please fix the validation errors in the form'))
        refToFocusOnError?.focus()
        return false
      }
    }

    return true
  }, [issueNode, issueTemplate, issueFormRef, onError, formRepository])

  const handleCreate = useCallback(() => {
    if (!issueNode || isLoading || isComplete) {
      return
    }

    // Perform validation if this is a single issue
    if (!issueNode.parent && issueNode.children.length === 0) {
      const isValid = validateSingleIssueCreation()
      if (!isValid) return
    }

    mutate()
  }, [isLoading, isComplete, mutate, issueNode, validateSingleIssueCreation])

  // Transform response data into the format expected by onSaveSuccess
  const transformResponseData = useCallback(
    (responseData: BulkCreateStatusResponse | undefined, rootNode: TreeNode<DraftOrExistingIssue> | undefined) => {
      if (!responseData || !rootNode) return []
      if (responseData.job.completed_issues.length === 0) return []

      const drafts = new Map<string, DraftOrExistingIssue>()
      visitDependencies(rootNode, (node: TreeNode<DraftOrExistingIssue>) => {
        drafts.set(node.item.tag, node.item)
      })

      return responseData.job.completed_issues.map(issue => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const draft = drafts.get(issue.tag)!
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const [owner, repo] = draft.repository!.split('/')

        return {
          draftType: draft.type,
          tag: issue.tag,
          parentTag: draft.parentTag,
          blockedByTags: draft.blockedByTags,
          isBlockingTags: draft.isBlockingTags,
          template: draft.type === 'new-issue' ? draft.template : undefined,
          repository: {
            databaseId: issue.repository_id,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            name: repo!,
            owner: {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              login: owner!,
            },
          },
          number: issue.number,
          title: draft.name,
          url: issue.href,
          assignees: 'assignees' in draft ? (draft.assignees ?? []) : [],
          errors: issue.errors || [],
        }
      })
    },
    [],
  )

  // Handle errors
  useEffect(() => {
    if (isError && error) {
      onError?.(new CreateError(error.message))
    }
  }, [isError, error, onError])

  // Handle successful creation
  useEffect(() => {
    if (!data || !isComplete) return

    const createdIssues = transformResponseData(data, issueNode)

    onComplete()
    void onSaveSuccess(createdIssues)
  }, [data, isComplete, issueNode, onComplete, onSaveSuccess, transformResponseData])

  return {
    handleCreate,
    isLoading,
    isComplete,
    isError,
    error,
    data,
  }
}
